import { Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxDropdownModule, NxDropdownSelectChange } from '@aposin/ng-aquila/dropdown';
import { FileItem } from '@aposin/ng-aquila/file-uploader';
import { FORMFIELD_DEFAULT_OPTIONS, FormfieldDefaultOptions, NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxHeadlineModule } from '@aposin/ng-aquila/headline';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxLinkModule } from '@aposin/ng-aquila/link';
import { NxMessageModule } from '@aposin/ng-aquila/message';
import { NX_MODAL_DATA } from '@aposin/ng-aquila/modal';
import { NxRadioModule } from '@aposin/ng-aquila/radio-button';
import { NxRadioToggleModule } from '@aposin/ng-aquila/radio-toggle';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';

import { DocumentHandler } from '#components/DocumentHandler';
import { FileUploaderComponent } from '#components/file-uploader/file-uploader.component';
import { NotificationsGroupComponent } from '#components/notifications-group/notifications-group.component';
import { ESG_GERL_QUIZ_COMMENT_TEMPLATE, esgGerlQuizCommentChangedValidator } from '#data/esg-gerl-quiz-comment';
import { AvailableSectors } from '#data/SensitiveBusinessSectors';
import { environment } from '#environments/environment';
import { Notification } from '#models/MicroController.model';
import CveService from '#services/cve/cve.service';
import { LineOfBusiness } from '#services/cve/VettingIteration';
import FeatureToggleService from '#services/featureToggle/featureToggle.service';
import UserService, { SupportedBuCode } from '#services/user/user.service';
import { formatDate } from '#utils/dateUtils';

export interface UpdateModalSubmitParams {
  isValidate: boolean;
  comment?: string;
  email?: string;
  documents?: FileItem[];
  documentIds?: string[];
  form?: Record<string, string | undefined>;
}

const formfieldDefaultOptions: FormfieldDefaultOptions = {
  appearance: 'outline',
  nxFloatLabel: 'always',
};

@UntilDestroy()
@Component({
  templateUrl: './sensitive-business-update-modal.component.html',
  imports: [
    NxHeadlineModule,
    NxFormfieldModule,
    FormsModule,
    NxInputModule,
    ReactiveFormsModule,
    NxButtonModule,
    NxRadioToggleModule,
    NxRadioModule,
    NxMessageModule,
    NotificationsGroupComponent,
    FileUploaderComponent,
    NxSpinnerModule,
    TranslateModule,
    NxLinkModule,
    NxDropdownModule,
  ],
  providers: [{ provide: FORMFIELD_DEFAULT_OPTIONS, useValue: formfieldDefaultOptions }],
})
export class SensitiveBusinessUpdateModalComponent extends DocumentHandler {
  readonly data = inject<{
    setShouldClose: (val: boolean) => void;
    submit: (submitParams: UpdateModalSubmitParams) => Promise<void>;
    notifications: Notification[];
    controllerI18n: string;
    closeModal: () => void;
    email?: string;
    comment?: string;
  }>(NX_MODAL_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(UserService);
  protected readonly featureToggleService = inject(FeatureToggleService);
  private readonly cveService = inject(CveService);

  protected comment?: string;
  protected loading = false;

  protected readonly ESG_SCORING_LINK = environment.esgScoringUrl;
  protected readonly isEsgScoringToolEnabled = computed(
    () =>
      this.userService.buCodeSignal() &&
      this.featureToggleService.getFeatureToggleSignal(
        `esgScoringTool_${this.userService.buCodeSignal() as SupportedBuCode}_${this.iterationData()?.lineOfBusiness as LineOfBusiness}`,
      )(),
  );

  protected readonly updateForm = this.fb.group({
    checkResponse: ['', Validators.required],
    esgQuizComment: [ESG_GERL_QUIZ_COMMENT_TEMPLATE],
  });

  protected readonly esgQuizForm: FormGroup<{
    sector: FormControl<keyof typeof AvailableSectors | null>;
    question: FormControl<string | null>;
    esgQuizComment: FormControl<string | null>;
  }> = this.fb.group({
    sector: ['' as keyof typeof AvailableSectors, Validators.required],
    question: [''],
    esgQuizComment: [ESG_GERL_QUIZ_COMMENT_TEMPLATE],
  });

  private readonly iterationData = toSignal(this.cveService.iterationData$);

  protected readonly sectors = computed(() =>
    this.isEsgScoringToolEnabled() ? undefined : Object.values(AvailableSectors),
  );

  private readonly sector = effect(async () => {
    const sensitiveBusinessSector = this.iterationData()?.customProperties.supportRequestData?.sensitiveBusinessSector;
    if (sensitiveBusinessSector) {
      this.esgQuizForm.patchValue({ sector: sensitiveBusinessSector as keyof typeof AvailableSectors });
    }
  });

  constructor() {
    super();
    this.comment = this.data.comment;

    const iterationData = this.iterationData();
    if (!iterationData) {
      throw new Error('No iteration data');
    }

    if (iterationData.customProperties.esgGerlQuizComment) {
      this.esgQuizForm.patchValue({ esgQuizComment: iterationData.customProperties.esgGerlQuizComment as string });
      this.updateForm.patchValue({ esgQuizComment: iterationData.customProperties.esgGerlQuizComment as string });
    }

    this.esgQuizForm.controls.question.valueChanges.subscribe((newValue) =>
      this.updateEsgQuizCommentValidity(newValue, this.esgQuizForm),
    );
    this.updateForm.controls.checkResponse.valueChanges.subscribe((newValue) =>
      this.updateEsgQuizCommentValidity(newValue, this.updateForm),
    );
  }

  private readonly updateEsgQuizCommentValidity = (
    newValue: string | null,
    form: typeof this.updateForm | typeof this.esgQuizForm,
  ) => {
    if (newValue === 'reject') {
      form.controls.esgQuizComment.setValidators([Validators.required, esgGerlQuizCommentChangedValidator]);
    } else {
      form.controls.esgQuizComment.clearValidators();
      form.controls.esgQuizComment.updateValueAndValidity();
    }
  };

  protected async sectorChanged(event: NxDropdownSelectChange) {
    if (!event.value) return;
    const newSector: keyof typeof AvailableSectors = event.value;
    this.esgQuizForm.patchValue({ sector: newSector as keyof typeof AvailableSectors });
    if (newSector === 'NONE') {
      this.esgQuizForm.controls.question.setValue('accept');
      this.esgQuizForm.controls.esgQuizComment.clearValidators();
      this.esgQuizForm.controls.esgQuizComment.updateValueAndValidity();
    } else {
      this.esgQuizForm.controls.question.setValue('');
      this.esgQuizForm.controls.esgQuizComment.setValidators([Validators.required, esgGerlQuizCommentChangedValidator]);
    }
  }

  protected get isSubmitDisabled() {
    if (this.isEsgScoringToolEnabled()) {
      const awaitedDocumentsLength = 1;
      const documentsCount = this.documents?.length ?? 0;
      return this.updateForm.invalid || this.loading || documentsCount < awaitedDocumentsLength;
    } else {
      return this.esgQuizForm.invalid || this.loading;
    }
  }

  protected async submit() {
    this.loading = true;
    this.data.setShouldClose(false);
    await this.data.submit({
      isValidate: this.isEsgScoringToolEnabled()
        ? this.updateForm.value.checkResponse === 'accept'
        : this.esgQuizForm.value.question === 'accept',
      comment: this.comment,
      documents: this.documents,
    });
    this.loading = false;
    this.data.setShouldClose(true);
    this.data.closeModal();
  }

  protected get todaysDate() {
    return formatDate(new Date().toString());
  }
}
